import React, { useEffect } from 'react';
import { AccountType, Country } from '../../../API/Enums';
import { useAppointmentState, useAppointmentUpdateAndPersist, useMaskPhone, useMaskEmail } from '../../../context/Appointment/appointment';
import { CTAButton } from '../../../controls/ButtonBase';
import { useLocalization } from '../../../context/Localization/localization';
import { DataBox, DataBoxItem } from '../../../controls/DataBox/DataBox';
import { Heading } from '../../../controls/Heading/Heading';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { formatAsPhoneNumber } from '../../../helpers/Number';
import { textCapitalize } from '../../../helpers/textCapitalize';
import { ScrollableBody } from '../../Layout/ScrollableBody/ScrollableBody';
import css from './ConfirmInformation.module.scss';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { getIsInformationMissing } from '../../../helpers/Appointment';
import { GMDialogError } from '../../../controls/Dialog';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';

interface IProps {
    /** Called when the user confirms the correct information */
    onInformationCorrect: () => void;
    /** Called when the user needs to change their information information */
    onInformationIncorrect: () => void;
    /** Called when customer already exists (Currently DT only) */
    onExistingCustomer: () => void;
    /** Called when customer information is missing */
    onInformationMissing: () => void;
}

export const ConfirmInformation: React.FC<IProps> = React.memo((props) => {
    const appointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const maskPhone = useMaskPhone();
    const maskEmail = useMaskEmail();
    const Localization = useLocalization();
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<any>();
    const [confirmed, setConfirmed] = React.useState(false);
    error && console.error(error);

    const {
        dealer: { country },
    } = useKioskState();
    const stateOrProvinceLocalized = country === Country.Canada ? Localization.Province : Localization.State;
    const postalCodeLocalized = country === Country.Canada ? Localization.PostalCode : Localization.ZipCode;
    const postalCode = country === Country.Canada ? appointment.postalCode.slice(0, 3) + ' ' + appointment.postalCode.slice(3) : appointment.postalCode;

    const getDataBox = () => {
        const dataBox: DataBoxItem[] = [
            {
                title: Localization.Street,
                value: appointment.street,
                case: 'capitalize',
            },
            {
                title: Localization.City,
                value: appointment.city,
                case: 'capitalize',
            },
            {
                title: stateOrProvinceLocalized,
                value: appointment.stateOrProvince,
                case: 'uppercase',
            },
            {
                title: postalCodeLocalized,
                value: postalCode,
            },
            {
                title: Localization.Phone,
                value: formatAsPhoneNumber(appointment.phone),
                masked: maskPhone,
            },
            {
                title: Localization.Email,
                value: appointment.email,
                masked: maskEmail,
            },
        ];

        if (appointment.accountType === AccountType.business) {
            const databoxBusinessName = appointment.businessName || '';
            dataBox.unshift({
                title: Localization.Business,
                value: textCapitalize(databoxBusinessName, true),
            });
        }
        else {
            dataBox.unshift({
                title: Localization.LastName,
                value: textCapitalize(appointment.lastName),
            });
            dataBox.unshift({
                title: Localization.FirstName,
                value: textCapitalize(appointment.firstName),
            });
        }

        return dataBox;
    };

    useEffect(() => {
        if (getIsInformationMissing(appointment)) {
            props.onInformationMissing();
        }
        // This is returned by the web service to indicate that the customer exists already (Currently DT only)
        if (appointment.dmsCustomerKey === '-1') {
            props.onExistingCustomer();
        }
        else if (confirmed) {
            props.onInformationCorrect();
        }
    });

    if (loading || confirmed) {
        return <Spinner />;
    }

    return (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={
                        <GMAppointmentName />
                    }
                    secondaryHeading={Localization.ConfirmAllInformation}
                    leftAlign
                />
            }
            proceedButtons={
                <GMButtonGroup
                    alignment={'center'}
                    buttons={[
                        (
                            <CTAButton
                                styleType={'primary'}
                                onClick={async () => {
                                    setLoading(true);
                                    await setAppointment(appointment, true, true)
                                        .catch(setError);
                                    setLoading(false);
                                    setConfirmed(true);
                                }}
                                title={Localization.ClickToConfirmCorrect}
                                label={Localization.YesItIs}
                                kioskOptions={{ square: true }}
                                id={Localization.ClickToConfirmCorrect}
                                ariaLabel={Localization.ClickToConfirmCorrect}
                            />
                        ),
                        (
                            <CTAButton
                                styleType={'secondary'}
                                onClick={() => props.onInformationIncorrect()}
                                title={Localization.ClickToReturnToEntry}
                                label={Localization.NoItIsNot}
                                kioskOptions={{
                                    square: true,
                                    squareOptions: {
                                        roundedIcon: (
                                            <GMRoundedIcon
                                                themeType={'primary'}
                                                allowInvertedPrimary
                                                roundedBackgroundSize={'large'}
                                            >
                                                <GMSizedIcon iconSize={'custom'} iconType={'No'} />
                                            </GMRoundedIcon>
                                        ),
                                    },
                                }}
                                id={Localization.ClickToReturnToEntry}
                                ariaLabel={Localization.ClickToReturnToEntry}
                            />
                        ),
                    ]}
                />
            }
            unsetButtons
            topDivider
        >
            <div className={css.dataBoxWrapper}>
                <DataBox dataItems={getDataBox()} />
            </div>
            <GMDialogError
                ariaLabel={Localization.AnErrorHasOccured}
                open={!!error}
                title={Localization.AnErrorHasOccured}
                onClose={() => setError(undefined)}
            >
                <div style={{ display: 'none' }}>{error}</div>
            </GMDialogError>
        </ScrollableBody>
    );
});
